import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlans } from "@/hooks/usePlans";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { track } from "@/lib/analytics";

// A tabela é montada a partir do que cada plano tem cadastrado em "recursos":
// FREE e PRO são casados por área + nome. Assim, ajustar um plano pelo admin
// muda a landing, sem código novo.
function montarAreas(plans) {
  const free = plans?.find((p) => p.key === "free" && p.active !== false) || null;
  const pro = plans?.find((p) => p.key === "pro_monthly") || null;
  const base = pro?.recursos || free?.recursos || [];
  if (!base.length) return { areas: [], temFree: Boolean(free) };

  const valorDe = (plano, item) =>
    (plano?.recursos || []).find((r) => r.area === item.area && r.nome === item.nome)?.valor ?? "—";

  const areas = [];
  base.forEach((item) => {
    const linha = {
      nome: item.nome,
      descricao: item.descricao,
      free: valorDe(free, item),
      pro: valorDe(pro, item),
    };
    const atual = areas.find((a) => a.area === item.area);
    if (atual) atual.linhas.push(linha);
    else areas.push({ area: item.area, linhas: [linha] });
  });
  return { areas, temFree: Boolean(free) };
}

const INCLUIDO = new Set(["Incluído", "Incluída"]);
const AUSENTE = new Set(["—", "-", ""]);

// "Incluído" e "—" viram símbolo; o limite do plano ("Até 100", "3× ao dia") é
// a informação que o advogado veio procurar, então vai por extenso.
function Valor({ texto, destaque }) {
  if (INCLUIDO.has(texto)) {
    return (
      <>
        <Check
          size={20}
          strokeWidth={1.5}
          aria-hidden="true"
          className={`inline-block ${destaque ? "text-[#003882]" : "text-[#335480]"}`}
        />
        <span className="sr-only">Incluído</span>
      </>
    );
  }
  if (!texto || AUSENTE.has(texto)) {
    return (
      <>
        <Minus size={20} strokeWidth={1.5} aria-hidden="true" className="inline-block text-[#6685AD]" />
        <span className="sr-only">Não incluído</span>
      </>
    );
  }
  return (
    <span className={destaque ? "font-semibold text-[#003882]" : "text-[#335480]"}>{texto}</span>
  );
}

export default function TabelaRecursos() {
  const navigate = useNavigate();
  const { plans, landingPricing } = usePlans();
  const { areas, temFree } = useMemo(() => montarAreas(plans), [plans]);
  const ref = useScrollReveal(0.15, areas.length > 0);
  const precos = landingPricing();

  if (!areas.length) return null;

  const precoFree = temFree ? precos?.free?.price || "R$0" : null;
  const precoPro = precos?.pro?.price;
  const assinar = (local, plano) => () => {
    track("cta_click", { location: local });
    track("signup_start", { location: local });
    navigate(plano ? `/registro?next=checkout&plan=${plano}` : "/registro");
  };

  // Colunas: nome do recurso + PRO, mais FREE quando o plano está ativo.
  const colunas = temFree ? 3 : 2;
  const celulaPro = "bg-[#e5edf5]";

  return (
    <section id="recursos" className="py-20 px-6 bg-[#F4F7FB]">
      <div className="max-w-4xl mx-auto">
        {/* A entrada fica no cabeçalho, não na seção: com 37 linhas ela passa
            de 4000px e nunca atinge os 15% de área visível que o observer
            exige — a tabela inteira ficaria em opacity 0. */}
        <div ref={ref} className="scroll-reveal text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
            O que vem em cada plano
          </h2>
          <p className="text-[#335480] text-lg mt-3">
            A lista completa, com os limites de cada plano. Sem letra miúda.
          </p>
        </div>

        {/* Desktop: tabela com cabeçalho fixo — em 37 linhas, saber de qual
            plano é a coluna importa mais do que o enfeite. */}
        <div className="hidden md:block bg-white border border-[#D1DEEE] rounded-xl">
          <table className="w-full text-base" data-testid="tabela-recursos">
            <thead className="sticky top-16 z-10">
              <tr>
                <th
                  scope="col"
                  className="text-left py-4 px-6 bg-white text-[#335480] font-medium text-sm uppercase tracking-wide border-b border-[#D1DEEE]"
                >
                  Recurso
                </th>
                {temFree && (
                  <th scope="col" className="w-44 py-3 px-4 bg-white border-b border-[#D1DEEE] align-bottom">
                    <span className="block text-[#001533] font-bold text-lg" style={{ fontFamily: "Cabinet Grotesk" }}>
                      Free
                    </span>
                    <span className="block text-[#335480] text-sm">{precoFree}/mês</span>
                  </th>
                )}
                <th scope="col" className={`w-44 py-3 px-4 border-b border-[#D1DEEE] align-bottom ${celulaPro}`}>
                  <span className="block text-[#003882] font-bold text-lg" style={{ fontFamily: "Cabinet Grotesk" }}>
                    Pro
                  </span>
                  <span className="block text-[#335480] text-sm">{precoPro ? `${precoPro}/mês` : " "}</span>
                </th>
              </tr>
            </thead>

            {areas.map((grupo) => (
              <tbody key={grupo.area}>
                <tr>
                  <th
                    scope="colgroup"
                    colSpan={colunas - 1}
                    className="text-left pt-9 pb-2 px-6 text-[#001533] font-semibold text-lg border-t border-[#D1DEEE]"
                    style={{ fontFamily: "Cabinet Grotesk" }}
                  >
                    {grupo.area}
                  </th>
                  <td className={`border-t border-[#D1DEEE] ${celulaPro}`} />
                </tr>
                {grupo.linhas.map((linha) => (
                  <tr key={`${grupo.area}-${linha.nome}`} className="group border-t border-[#D1DEEE]">
                    <th scope="row" className="text-left py-4 px-6 font-normal group-hover:bg-[#EBF1F8]">
                      <span className="block text-[#001533] font-medium">{linha.nome}</span>
                      {linha.descricao && (
                        <span className="block text-[#335480] text-sm mt-0.5">{linha.descricao}</span>
                      )}
                    </th>
                    {temFree && (
                      <td className="text-center py-4 px-4 group-hover:bg-[#EBF1F8]">
                        <Valor texto={linha.free} />
                      </td>
                    )}
                    <td className={`text-center py-4 px-4 ${celulaPro}`}>
                      <Valor texto={linha.pro} destaque />
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}

            {/* Depois de percorrer a lista inteira, assinar não deve exigir
                rolar de volta até os cards de preço. */}
            <tfoot>
              <tr className="border-t border-[#D1DEEE]">
                <td className="py-5 px-6" />
                {temFree && (
                  <td className="py-5 px-4 align-middle">
                    <Button
                      data-testid="recursos-free-btn"
                      onClick={assinar("tabela_recursos_free")}
                      variant="outline"
                      className="w-full h-11 border-[#003882] text-[#003882] hover:bg-[#e5edf5] font-semibold"
                    >
                      Assinar START
                    </Button>
                  </td>
                )}
                <td className={`py-5 px-4 align-middle ${celulaPro}`}>
                  <Button
                    data-testid="recursos-pro-btn"
                    onClick={assinar("tabela_recursos_pro", "pro_monthly")}
                    className="w-full h-11 bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
                  >
                    Assinar o Pro
                  </Button>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile: a mesma informação empilhada. Tabela com rolagem lateral
            esconde justamente a coluna que responde a pergunta do visitante. */}
        <div className="md:hidden space-y-6" data-testid="recursos-mobile">
          {areas.map((grupo) => (
            <div key={grupo.area} className="bg-white border border-[#D1DEEE] rounded-xl overflow-hidden">
              <h3
                className="px-5 pt-5 pb-1 text-[#001533] font-semibold text-lg"
                style={{ fontFamily: "Cabinet Grotesk" }}
              >
                {grupo.area}
              </h3>
              <dl className="divide-y divide-[#D1DEEE]">
                {grupo.linhas.map((linha) => (
                  <div key={`${grupo.area}-${linha.nome}`} className="px-5 py-4">
                    <dt className="text-[#001533] font-medium">{linha.nome}</dt>
                    {linha.descricao && (
                      <p className="text-[#335480] text-sm mt-0.5">{linha.descricao}</p>
                    )}
                    <dd className="mt-3 flex gap-2">
                      {temFree && (
                        <span className="flex-1 flex items-center justify-between gap-2 rounded-lg border border-[#D1DEEE] px-3 py-2">
                          <span className="text-[#335480] text-sm font-medium">Free</span>
                          <Valor texto={linha.free} />
                        </span>
                      )}
                      <span className="flex-1 flex items-center justify-between gap-2 rounded-lg bg-[#e5edf5] px-3 py-2">
                        <span className="text-[#003882] text-sm font-semibold">Pro</span>
                        <Valor texto={linha.pro} destaque />
                      </span>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
          <Button
            data-testid="recursos-pro-btn-mobile"
            onClick={assinar("tabela_recursos_pro", "pro_monthly")}
            className="w-full h-12 bg-[#003882] hover:bg-[#00285e] text-white font-semibold text-base"
          >
            Assinar o Pro{precoPro ? ` — ${precoPro}/mês` : ""}
          </Button>
        </div>
      </div>
    </section>
  );
}
